import React, { useEffect, useState } from "react";
import { getTiersDuplicates, mergeTiers } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { X, Loader2, GitMerge, Mail, Users, Link2, AlertTriangle } from "lucide-react";
import { log } from "../../../lib/log";

const Group = ({ g, onMerged }) => {
  const [keep, setKeep] = useState(g.keep_id);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const { toast } = useToast();
  const merge = async () => {
    const dups = g.items.filter((t) => t.id !== keep).map((t) => t.id);
    if (!window.confirm(`Fusionner ${dups.length} doublon(s) dans « ${g.items.find((t) => t.id === keep)?.name} » (#${keep}) ?\nContacts, événements agenda et références CRM seront rattachés au tiers conservé, puis les doublons supprimés de Dolibarr.`)) return;
    setBusy(true);
    try {
      const { data } = await mergeTiers(keep, dups);
      setReport(data);
      toast({ title: data.ok ? "Fusion terminée ✓" : "Fusion partielle", description: data.ok ? `${dups.length} doublon(s) supprimé(s)` : "Certains doublons n'ont pas pu être supprimés (voir détail)", variant: data.ok ? undefined : "destructive" });
      onMerged();
    } catch (e) { toast({ title: "Fusion impossible", description: e?.response?.data?.detail || "Erreur", variant: "destructive" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-neutral-200 rounded-xl p-4" data-testid={`dup-group-${g.items[0].id}`}>
      <p className="text-xs text-neutral-500 mb-2 flex items-center gap-1">{g.reason.includes("email") ? <Mail size={12} /> : <Users size={12} />} {g.items.length} tiers · {g.reason} · <span className="font-mono">{g.key.replace("name:", "")}</span></p>
      <div className="space-y-1.5">
        {g.items.map((t) => (
          <label key={t.id} className={`flex items-center gap-3 rounded-lg px-3 py-2 cursor-pointer border ${keep === t.id ? "border-brand bg-brand/5" : "border-transparent hover:bg-neutral-50"}`} data-testid={`dup-item-${t.id}`}>
            <input type="radio" name={`keep-${g.key}`} className="accent-brand" checked={keep === t.id} onChange={() => setKeep(t.id)} disabled={!!report} />
            <span className="min-w-0 flex-1"><span className="text-sm font-semibold text-neutral-900">{t.name}</span> <span className="text-xs text-neutral-400">#{t.id}</span>
              <span className="block text-xs text-neutral-500">{t.email || "sans email"}{t.town && ` · ${t.town}`}{t.date_creation && ` · créé le ${new Date(Number(t.date_creation) * 1000 || t.date_creation).toLocaleDateString("fr-FR")}`}</span></span>
            <span className="text-[11px] text-neutral-500 flex items-center gap-1 shrink-0" title="Références CRM (prospects, interventions, contrats, tickets, contrôles)"><Link2 size={11} /> {t.local_refs}</span>
            {keep === t.id && <span className="text-[11px] font-semibold text-brand shrink-0">conservé</span>}
          </label>
        ))}
      </div>
      {report ? (
        <ul className="mt-3 text-xs space-y-0.5" data-testid="dup-report">
          {report.merged.map((r) => <li key={r.id} className={r.deleted ? "text-emerald-700" : "text-red-600"}>#{r.id} : {r.deleted ? `supprimé · ${r.contacts} contact(s), ${r.events} événement(s), ${r.local} référence(s) CRM déplacés` : `non supprimé — ${r.error}`}</li>)}
        </ul>
      ) : (
        <div className="flex justify-end mt-3">
          <button onClick={merge} disabled={busy} className="bg-brand hover:bg-brand-dark text-white text-xs font-semibold px-3 py-2 rounded-md flex items-center gap-1.5 disabled:opacity-60" data-testid={`dup-merge-${g.items[0].id}`}>{busy ? <Loader2 size={13} className="animate-spin" /> : <GitMerge size={13} />} Fusionner dans #{keep}</button>
        </div>
      )}
    </div>
  );
};

/* Master-only modal: detect and merge duplicate Dolibarr third parties. */
const TiersDuplicates = ({ onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = () => getTiersDuplicates().then((r) => setData(r.data)).catch((e) => { log.error(e); setErr(e?.response?.data?.detail || "Chargement impossible"); });
  useEffect(() => { load(); }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="tiers-duplicates">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-6 shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div><h3 className="font-heading font-bold text-lg text-neutral-900 flex items-center gap-2"><GitMerge size={18} className="text-brand" /> Doublons de tiers</h3>
            <p className="text-xs text-neutral-500">Tiers Dolibarr partageant le même email ou le même nom. Choisissez celui à conserver : contacts, agenda et références CRM y sont rattachés, les autres sont supprimés.</p></div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="tiers-duplicates-close"><X size={18} /></button>
        </div>
        {err && <p className="text-sm text-red-600 flex items-center gap-1"><AlertTriangle size={14} /> {err}</p>}
        {!data && !err && <p className="text-sm text-neutral-500 flex items-center gap-2 py-6 justify-center"><Loader2 size={16} className="animate-spin" /> Analyse des tiers…</p>}
        {data && (data.groups.length === 0 ? <p className="text-sm text-emerald-700 text-center py-6" data-testid="tiers-duplicates-none">Aucun doublon détecté.</p> : (
          <div className="space-y-3">
            <p className="text-sm text-neutral-700" data-testid="tiers-duplicates-count"><b>{data.total_duplicates}</b> doublon(s) dans {data.groups.length} groupe(s)</p>
            {data.groups.map((g) => <Group key={g.key} g={g} onMerged={() => { onChanged(); }} />)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TiersDuplicates;
